import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'

import { ServiceOrderSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { NewOrderForm } from '#admin/features/exhibitor/new-order-form.tsx'
import {
  applicationsQuery,
  serviceItemsQuery,
  serviceOrderQuery,
  servicePolicyQuery,
} from '#admin/features/exhibitor/queries.ts'
import { serviceOrderActions } from '#admin/features/exhibitor/service-orders-api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/exhibition/service-orders/$serviceOrderId/edit` — the operator changes a draft order: its
 * services, its placement and its discount. Not nested in the order page, so it is a page of its
 * own and not a part of that page.
 */
export const Route = createFileRoute(
  '/_authenticated/exhibition/service-orders/$serviceOrderId_/edit',
)({
  pendingComponent: ServiceOrderSkeleton,
  staticData: { crumb: 'Edit order' },
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(serviceOrderQuery(params.serviceOrderId)),
      context.queryClient.ensureQueryData(applicationsQuery()),
      context.queryClient.ensureQueryData(serviceItemsQuery()),
      context.queryClient.ensureQueryData(servicePolicyQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: EditOrderPage,
})

function EditOrderPage() {
  const { serviceOrderId } = Route.useParams()
  const navigate = useNavigate()
  const { data: order } = useSuspenseQuery(serviceOrderQuery(serviceOrderId))
  const { data: applications } = useSuspenseQuery(applicationsQuery())
  const { data: catalog } = useSuspenseQuery(serviceItemsQuery())
  const { data: policy } = useSuspenseQuery(servicePolicyQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())

  // A submitted order cannot change. An old link goes back to the order page.
  if (!serviceOrderActions(order).edit) {
    return <Navigate to="/exhibition/service-orders/$serviceOrderId" params={{ serviceOrderId }} />
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Edit the order of ${order.companyName}`}
        description="Change the services, the placement or the discount. The order stays a draft until you submit it."
      />
      <NewOrderForm
        applications={applications.items.filter((a) => a.id === order.applicationId)}
        catalog={catalog.items}
        policy={policy}
        zone={manifest?.tenant.timezone ?? 'UTC'}
        initialApplicationId={order.applicationId}
        editing={order}
        onCreated={(id) =>
          void navigate({
            to: '/exhibition/service-orders/$serviceOrderId',
            params: { serviceOrderId: id },
          })
        }
      />
    </div>
  )
}
